import { getSmilePhotoApiUrl } from '../config/api';

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export interface SmilePhotoUploadResult {
    file: string;
    ext: string;
    token: string;
}

/**
 * Phone photos are often 5-12 MB. Shrinking in the browser keeps the upload
 * fast on mobile data. Falls back to the original file if the browser cannot
 * decode it (e.g. HEIC on some desktop browsers); the server re-checks either way.
 */
async function downscale(file: File): Promise<Blob> {
    try {
        const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
        const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close?.();
        const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
        return blob && blob.size < file.size ? blob : file;
    } catch {
        return file;
    }
}

/** Throws an Error with a visitor-facing message on failure. */
export async function uploadSmilePhoto(botId: string, file: File): Promise<SmilePhotoUploadResult> {
    const body = await downscale(file);
    if (body.size > MAX_UPLOAD_BYTES) {
        throw new Error('This photo is too large. Please choose one under 10MB.');
    }

    const form = new FormData();
    form.append('photo', body, body === file ? file.name : 'smile.jpg');

    let response: Response;
    try {
        response = await fetch(getSmilePhotoApiUrl(botId), { method: 'POST', body: form });
    } catch {
        throw new Error('Could not upload your photo. Please check your connection and try again.');
    }

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
        if (response.status === 429) throw new Error('Too many uploads. Please wait a few minutes and try again.');
        throw new Error(data?.error || 'Could not upload your photo. Please try again.');
    }
    return { file: data.file, ext: data.ext, token: data.token };
}
